import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Loader2, Wand2, CheckCircle, AlertTriangle, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function AssessmentTab({ profile, update }) {
  const [assessing, setAssessing] = useState(false);
  const [assessment, setAssessment] = useState(profile.assessment || null);

  const run = async () => {
    setAssessing(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/aspiring/assess`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ profile })
      });
      if (r.ok) { 
        const data = await r.json();
        setAssessment(data.assessment); 
        update("assessment", data.assessment);
        toast.success("Done!"); 
      }
    } catch { toast.error("Failed"); }
    setAssessing(false);
  };

  const scores = {
    profile: (profile.linkedin_url ? 25 : 0) + (profile.preferred_role ? 25 : 0) + (profile.skills?.length ? 25 : 0) + (profile.your_edge ? 25 : 0),
    cofounder: profile.no_cofounder ? 50 : ((profile.cofounder_name ? 50 : 0) + (profile.how_you_met ? 50 : 0)),
    idea: profile.no_idea ? 0 : ((profile.therapeutic_area ? 25 : 0) + (profile.market_opportunity ? 50 : 0) + (profile.validation_done ? 25 : 0)),
    solution: profile.no_idea ? 0 : ((profile.solution_description ? 50 : 0) + (profile.defensibility ? 50 : 0))
  };
  scores.overall = Math.round((scores.profile + scores.cofounder + scores.idea + scores.solution) / 4);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold">Assessment</h2>
        <Button onClick={run} disabled={assessing} className="bg-amber-500 hover:bg-amber-600">
          {assessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wand2 className="w-4 h-4 mr-2" />}AI Assessment
        </Button>
      </div>
      <Card><CardContent className="p-4">
        <div className="text-center mb-6">
          <div className={`text-5xl font-bold ${scores.overall >= 70 ? "text-green-600" : scores.overall >= 40 ? "text-amber-600" : "text-red-500"}`}>{scores.overall}%</div>
          <p className="text-slate-500 mt-1">Readiness</p>
        </div>
        <div className="space-y-3">
          {[["Profile", scores.profile], ["Cofounder", scores.cofounder], ["Idea", scores.idea], ["Solution", scores.solution]].map(([l, s]) => (
            <div key={l}><div className="flex justify-between text-sm mb-1"><span>{l}</span><span>{s}%</span></div><Progress value={s} className="h-2" /></div>
          ))}
        </div>
      </CardContent></Card>
      {assessment && (
        <Card><CardContent className="p-4 space-y-4">
          {assessment.strengths?.length > 0 && <div><h4 className="font-semibold text-green-700 flex items-center gap-2"><CheckCircle className="w-4 h-4" />Strengths</h4><ul className="mt-2 text-sm">{assessment.strengths.map((s, i) => <li key={i}>• {s}</li>)}</ul></div>}
          {assessment.risks?.length > 0 && <div><h4 className="font-semibold text-red-600 flex items-center gap-2"><AlertTriangle className="w-4 h-4" />Risks</h4><ul className="mt-2 text-sm">{assessment.risks.map((r, i) => <li key={i}>• {r}</li>)}</ul></div>}
          {assessment.focus_areas?.length > 0 && <div><h4 className="font-semibold text-amber-600 flex items-center gap-2"><TrendingUp className="w-4 h-4" />Focus</h4><ul className="mt-2 text-sm">{assessment.focus_areas.map((f, i) => <li key={i}>{i+1}. {f}</li>)}</ul></div>}
          {assessment.overall_feedback && <div className="p-3 bg-slate-50 rounded text-sm">{assessment.overall_feedback}</div>}
        </CardContent></Card>
      )}
    </div>
  );
}
